import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateShoreDeptTypeRequest,
    ShoreDeptType,
    ShoreDeptTypeRequest,
    ShoreDeptTypeResponse,
    UpdateShoreDeptTypeRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-dept-type.api.ts';
import {
    createApiShoreDeptType,
    deleteApiShoreDeptType,
    getApiShoreDeptType,
    updateApiShoreDeptType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-dept-type.api.ts';
import { extractError } from '@utils/slice.ts';

export interface ShoreDeptTypeError {
    message?: string;
    [k: string]: any;
}

export interface ShoreDeptTypeState {
    error: ShoreDeptTypeError | null;

    commonShoreDeptType: ShoreDeptTypeResponse | null;
    shoreDeptTypes: ShoreDeptType[];
    pagination: CommonTablePaginatedResponse;

    updatedShoreDeptType: ShoreDeptType | null;
    currentGetRequestId: string | null;

    isGetShoreDeptTypePending: boolean;
    isCreateShoreDeptTypePending: boolean;
    isUpdateShoreDeptTypePending: boolean;
    isDeleteShoreDeptTypePending: boolean;
}

const initialState: ShoreDeptTypeState = {
    error: null,

    commonShoreDeptType: null,
    shoreDeptTypes: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedShoreDeptType: null,
    currentGetRequestId: null,

    isGetShoreDeptTypePending: false,
    isCreateShoreDeptTypePending: false,
    isUpdateShoreDeptTypePending: false,
    isDeleteShoreDeptTypePending: false,
};

export const getShoreDeptTypes = createAsyncThunk<
    ShoreDeptTypeResponse,
    { clientId: number; tenantAlias: string; payload: ShoreDeptTypeRequest },
    { rejectValue: ShoreDeptTypeError }
>('adminCenterShoreDeptType/getShoreDeptTypes', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiShoreDeptType(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreDeptType = createAsyncThunk<
    ShoreDeptType,
    { clientId: number; tenantAlias: string; payload: CreateShoreDeptTypeRequest },
    { rejectValue: ShoreDeptTypeError }
>('adminCenterShoreDeptType/createShoreDeptType', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiShoreDeptType(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreDeptType = createAsyncThunk<
    ShoreDeptType,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateShoreDeptTypeRequest },
    { rejectValue: ShoreDeptTypeError }
>('adminCenterShoreDeptType/updateShoreDeptType', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiShoreDeptType(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreDeptType = createAsyncThunk<
    ShoreDeptType,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ShoreDeptTypeError }
>('adminCenterShoreDeptType/deleteShoreDeptType', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiShoreDeptType(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreDeptTypeSlice = createSlice({
    name: 'adminCenterShoreDeptType',
    initialState,
    reducers: {
        resetShoreDeptTypeState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getShoreDeptTypes.pending, (state, action) => {
            state.isGetShoreDeptTypePending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getShoreDeptTypes.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreDeptTypePending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonShoreDeptType = action.payload;
            state.shoreDeptTypes = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getShoreDeptTypes.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreDeptTypePending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createShoreDeptType.pending, (state) => {
            state.isCreateShoreDeptTypePending = true;
            state.error = null;
        });
        builder.addCase(createShoreDeptType.fulfilled, (state, action) => {
            state.isCreateShoreDeptTypePending = false;
            state.updatedShoreDeptType = action.payload;
        });
        builder.addCase(createShoreDeptType.rejected, (state, action) => {
            state.isCreateShoreDeptTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreDeptType.pending, (state) => {
            state.isUpdateShoreDeptTypePending = true;
            state.error = null;
        });
        builder.addCase(updateShoreDeptType.fulfilled, (state, action) => {
            state.isUpdateShoreDeptTypePending = false;
            state.updatedShoreDeptType = action.payload;
        });
        builder.addCase(updateShoreDeptType.rejected, (state, action) => {
            state.isUpdateShoreDeptTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreDeptType.pending, (state) => {
            state.isDeleteShoreDeptTypePending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreDeptType.fulfilled, (state, action) => {
            state.isDeleteShoreDeptTypePending = false;
            state.updatedShoreDeptType = action.payload;
            state.shoreDeptTypes = state.shoreDeptTypes.filter(
                (shoreDeptType) => shoreDeptType.id !== action.payload.id,
            );
        });
        builder.addCase(deleteShoreDeptType.rejected, (state, action) => {
            state.isDeleteShoreDeptTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { shoreDepartmentTypeResponse } = action.payload;

            if (!shoreDepartmentTypeResponse) return;

            const { data, pagination } = shoreDepartmentTypeResponse;
            state.commonShoreDeptType = shoreDepartmentTypeResponse;
            state.shoreDeptTypes = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetShoreDeptTypeState } = shoreDeptTypeSlice.actions;
export default shoreDeptTypeSlice.reducer;
